import type { InvoiceStatus, PaymentDetails } from '@/shared/api/types'
import { coinOf } from '@/shared/lib/currencies'
import { subtractDecimal } from '@/shared/lib/decimal'
import { crypto } from '@/shared/lib/format'
import { CheckIcon, ClockIcon, InfoIcon } from '@/shared/ui/icons'
import styles from './Payment.module.css'

interface Props {
  status: InvoiceStatus
  payment: PaymentDetails | null
  invoiceId: string
}

/**
 * Итог заявки. Адреса и QR здесь НЕТ ни в одном из состояний.
 *
 * Плательщик возвращается на эту страницу именно затем, чтобы
 * убедиться, что перевод дошёл. Увидев адрес с кнопкой «скопировать»,
 * заметная часть людей платит второй раз — а второй перевод уже
 * ни к какому заказу не привязан: адрес вернулся в общий пул.
 */
export function Settled({ status, payment, invoiceId }: Props) {
  const view = VIEWS[status] ?? EXPIRED
  const ticker = payment ? coinOf(payment.currency).ticker : ''
  const extra = status === 'overpaid' && payment
    ? subtractDecimal(payment.paid_amount, payment.crypto_amount)
    : null
  const missing = status === 'underpaid' && payment
    ? subtractDecimal(payment.crypto_amount, payment.paid_amount)
    : null

  return (
    <div className={styles.settled}>
      <span className={`${styles.settledMark} ${styles[view.tone]}`}>{view.icon}</span>
      <span className={styles.settledTitle}>{view.title}</span>

      {payment && status !== 'expired' ? (
        <span className={styles.settledSum}>
          Получено {crypto(payment.paid_amount)} {ticker}
        </span>
      ) : null}

      {/* Разницу называем числом, а не «переплатой»: продавцу
          придётся возвращать ровно эту сумму, и спорить о ней
          проще, когда она на экране у обоих. */}
      {extra ? (
        <span className={styles.settledSum}>Больше запрошенного на {crypto(extra)} {ticker}</span>
      ) : null}
      {missing ? (
        <span className={styles.settledSum}>Не хватило {crypto(missing)} {ticker}</span>
      ) : null}

      <span className={styles.settledId}>Номер заявки: {invoiceId}</span>
    </div>
  )
}

interface View {
  tone: 'ok' | 'warn' | 'bad'
  icon: React.ReactNode
  title: string
}

const EXPIRED: View = {
  tone: 'bad', icon: <ClockIcon size={28} />,
  title: 'Время на оплату истекло',
}

const VIEWS: Partial<Record<InvoiceStatus, View>> = {
  paid: {
    tone: 'ok', icon: <CheckIcon size={28} />,
    title: 'Оплачено',
  },
  overpaid: {
    tone: 'ok', icon: <CheckIcon size={28} />,
    title: 'Оплачено',
  },
  underpaid: {
    tone: 'warn', icon: <InfoIcon size={28} />,
    title: 'Оплата неполная',
  },
  expired: EXPIRED,
}
